/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 *
 * Interactive Cards Effect:
 * Based on CodePen by z- (Zewen)
 * Source: https://codepen.io/z-/pen/OBPJKK
 * License: CodePen License (typically MIT/Open Source) - Used with attribution
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import "./Realms.css";
import "./RealmTransitions.css";

const Realms = () => {
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [activeSlideshow, setActiveSlideshow] = useState(0); // 0 for first card
  const currentSlideRef = useRef(currentSlide);
  const [secondCardSlide, setSecondCardSlide] = useState(0);
  const secondCardSlideRef = useRef(secondCardSlide);
  const [thirdCardSlide, setThirdCardSlide] = useState(0);
  const thirdCardSlideRef = useRef(thirdCardSlide);
  const [fourthCardSlide, setFourthCardSlide] = useState(0);
  const fourthCardSlideRef = useRef(fourthCardSlide);
  const [fifthCardSlide, setFifthCardSlide] = useState(0);
  const fifthCardSlideRef = useRef(fifthCardSlide);
  const [showVideo, setShowVideo] = useState(false);

  // Video configurations for each realm
  const realmVideos = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/videos/Obsidian.mp4`, // Obsidian Nocturne
      `${process.env.PUBLIC_URL}/videos/Verdigris.mp4`, // The Verdigris Depths
      `${process.env.PUBLIC_URL}/videos/Embercoil.mp4`, // Embercoil Drift
      `${process.env.PUBLIC_URL}/videos/Nebula.mp4`, // Swirling nebula skies
      `${process.env.PUBLIC_URL}/videos/Thornreach.mp4`, // Thornreach Wilds
    ],
    []
  );

  // Use useMemo to prevent recreation of arrays on each render
  const slideshowImages = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/images/realms/realms1.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms2.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms3.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms4.png`,
    ],
    []
  );

  const secondCardImages = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/images/realms/realms64.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms65.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms66.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms67.png`,
    ],
    []
  );

  const thirdCardImages = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/images/realms/realms51.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms52.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms53.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms54.png`,
    ],
    []
  );

  const fourthCardImages = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/images/realms/realms25.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms26.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms38.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms39.png`,
    ],
    []
  );

  const fifthCardImages = useMemo(
    () => [
      `${process.env.PUBLIC_URL}/images/realms/realms81.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms82.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms83.png`,
      `${process.env.PUBLIC_URL}/images/realms/realms84.png`,
    ],
    []
  );

  // Slideshow configurations
  const slideshowConfigs = useMemo(
    () => [
      {
        interval: 3000,
        setSlide: setCurrentSlide,
        slideRef: currentSlideRef,
        images: slideshowImages,
      },
      {
        interval: 4000,
        setSlide: setSecondCardSlide,
        slideRef: secondCardSlideRef,
        images: secondCardImages,
      },
      {
        interval: 4500,
        setSlide: setThirdCardSlide,
        slideRef: thirdCardSlideRef,
        images: thirdCardImages,
      },
      {
        interval: 3500,
        setSlide: setFourthCardSlide,
        slideRef: fourthCardSlideRef,
        images: fourthCardImages,
      },
      {
        interval: 5000,
        setSlide: setFifthCardSlide,
        slideRef: fifthCardSlideRef,
        images: fifthCardImages,
      },
    ],
    [slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages]
  );

  // Keep ref in sync with state
  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  useEffect(() => {
    secondCardSlideRef.current = secondCardSlide;
  }, [secondCardSlide]);

  useEffect(() => {
    thirdCardSlideRef.current = thirdCardSlide;
  }, [thirdCardSlide]);

  useEffect(() => {
    fourthCardSlideRef.current = fourthCardSlide;
  }, [fourthCardSlide]);

  useEffect(() => {
    fifthCardSlideRef.current = fifthCardSlide;
  }, [fifthCardSlide]);

  // Handle card click using React state instead of DOM manipulation
  const handleCardClick = useCallback(
    (event, cardIndex) => {
      event.preventDefault();
      event.stopPropagation();

      // If clicking on indicators, don't process card activation
      if (event.target.classList.contains("indicator")) {
        return;
      }

      // If card is not active, make it active but don't open modal yet
      if (activeCardIndex !== cardIndex) {
        setActiveCardIndex(cardIndex);
        setActiveSlideshow(cardIndex);
        console.log(`Card ${cardIndex} activated, click again to enlarge`);
        return;
      }

      // Card is already active, now open the modal
      console.log(`Opening modal for card ${cardIndex}`);

      // Set modal image based on active card
      const slideStates = [
        currentSlideRef.current,
        secondCardSlideRef.current,
        thirdCardSlideRef.current,
        fourthCardSlideRef.current,
        fifthCardSlideRef.current,
      ];

      setModalImageIndex(slideStates[cardIndex]);
      setActiveSlideshow(cardIndex);
      setShowVideo(false); // Reset video state when opening modal
      setIsModalOpen(true);
    },
    [activeCardIndex]
  );

  // Unified slideshow effect for all cards
  useEffect(() => {
    const intervals = [];

    // Only run slideshows if modal is closed
    if (!isModalOpen) {
      slideshowConfigs.forEach((config, index) => {
        const intervalId = setInterval(() => {
          // Add transitioning class before changing the slide
          const card = document.querySelector(`.realms-options .realms-option:nth-child(${index + 1})`);
          if (card) {
            card.classList.add("transitioning");
            setTimeout(() => {
              config.setSlide((prev) => (prev + 1) % config.images.length);
              setTimeout(() => {
                card.classList.remove("transitioning");
              }, 600);
            }, 100);
          } else {
            config.setSlide((prev) => (prev + 1) % config.images.length);
          }
        }, config.interval);

        intervals.push(intervalId);
      });
    }

    // Clear all intervals when component unmounts or dependencies change
    return () => {
      intervals.forEach((intervalId) => clearInterval(intervalId));
    };
  }, [isModalOpen, slideshowConfigs]);

  // Modal navigation functions
  const closeModal = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        console.log("Modal close triggered by event", e.type);
      } else {
        console.log("Modal close triggered programmatically");
      }

      // Sync the slideshow with the modal image index when closing
      const slideSetters = [
        setCurrentSlide,
        setSecondCardSlide,
        setThirdCardSlide,
        setFourthCardSlide,
        setFifthCardSlide,
      ];

      if (slideSetters[activeSlideshow]) {
        slideSetters[activeSlideshow](modalImageIndex);
      }

      setShowVideo(false); // Reset video state when closing modal
      setIsModalOpen(false);
      console.log("Modal closed");

      // Re-enable body scroll
      document.body.style.overflow = "unset";
    },
    [modalImageIndex, activeSlideshow]
  );

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const nextImage = useCallback(() => {
    const imageArrays = [slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages];
    const currentArray = imageArrays[activeSlideshow] || slideshowImages;

    // If we're at the last image, show the video instead of cycling
    if (modalImageIndex === currentArray.length - 1) {
      setShowVideo(true);
    } else {
      setModalImageIndex((prev) => (prev + 1) % currentArray.length);
      setShowVideo(false);
    }
  }, [
    activeSlideshow,
    modalImageIndex,
    slideshowImages,
    secondCardImages,
    thirdCardImages,
    fourthCardImages,
    fifthCardImages,
  ]);

  const prevImage = useCallback(() => {
    const imageArrays = [slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages];
    const currentArray = imageArrays[activeSlideshow] || slideshowImages;

    // If we're showing video, go back to last image
    if (showVideo) {
      setShowVideo(false);
      setModalImageIndex(currentArray.length - 1);
    } else {
      setModalImageIndex((prev) => (prev - 1 + currentArray.length) % currentArray.length);
    }
  }, [
    activeSlideshow,
    showVideo,
    slideshowImages,
    secondCardImages,
    thirdCardImages,
    fourthCardImages,
    fifthCardImages,
  ]);

  // Handle modal indicator click (for both images and video)
  const handleModalIndicatorClick = useCallback(
    (index) => {
      const imageArrays = [slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages];
      const currentArray = imageArrays[activeSlideshow] || slideshowImages;

      // If clicking on the video indicator (index equals array length)
      if (index === currentArray.length) {
        setShowVideo(true);
        setModalImageIndex(currentArray.length - 1); // Set to last image index for consistency
      } else {
        setShowVideo(false);
        setModalImageIndex(index);
      }
    },
    [activeSlideshow, slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages]
  );

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (isModalOpen) {
        switch (event.key) {
          case "Escape":
            closeModal();
            break;
          case "ArrowLeft":
            prevImage();
            break;
          case "ArrowRight":
            nextImage();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isModalOpen, closeModal, nextImage, prevImage]);

  return (
    <div className="realms-section" id="realms">
      <div className="realms-container">
        <h2 className="realms-title">
          <span>Realms</span>
        </h2>
        <div className="realms-options">
          <div
            className={`realms-option realms-slideshow ${activeCardIndex === 0 ? "active" : ""}`}
            style={{
              "--optionBackground": `url(${slideshowImages[currentSlide]})`,
            }}
            onClick={(e) => handleCardClick(e, 0)}
          >
            <div className="realms-shadow"></div>
            <div className="realms-label">
              <div className="realms-icon">
                <img src={`${process.env.PUBLIC_URL}/images/icons/obsidian-icon.png`} alt="Obsidian Nocturne Icon" />
              </div>
              <div className="realms-info">
                <div className="realms-main">Obsidian Nocturne</div>
                <div className="realms-sub"></div>
              </div>
            </div>
            <div className="slideshow-indicators">
              {slideshowImages.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === currentSlide ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentSlide(index);
                  }}
                />
              ))}
            </div>
          </div>

          <div
            className={`realms-option realms-slideshow ${activeCardIndex === 1 ? "active" : ""}`}
            style={{
              "--optionBackground": `url(${secondCardImages[secondCardSlide]})`,
            }}
            onClick={(e) => handleCardClick(e, 1)}
          >
            <div className="realms-shadow"></div>
            <div className="realms-label">
              <div className="realms-icon">
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/verdigris-icon.png`}
                  alt="The Verdigris Depths Icon"
                />
              </div>
              <div className="realms-info">
                <div className="realms-main">The Verdigris Depths</div>
                <div className="realms-sub"></div>
              </div>
            </div>
            <div className="slideshow-indicators">
              {secondCardImages.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === secondCardSlide ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSecondCardSlide(index);
                  }}
                />
              ))}
            </div>
          </div>

          <div
            className={`realms-option realms-slideshow ${activeCardIndex === 2 ? "active" : ""}`}
            style={{
              "--optionBackground": `url(${thirdCardImages[thirdCardSlide]})`,
            }}
            onClick={(e) => handleCardClick(e, 2)}
          >
            <div className="realms-shadow"></div>
            <div className="realms-label">
              <div className="realms-icon">
                <img src={`${process.env.PUBLIC_URL}/images/icons/embercoil-icon.png`} alt="Embercoil Drift Icon" />
              </div>
              <div className="realms-info">
                <div className="realms-main">Embercoil Drift</div>
                <div className="realms-sub"></div>
              </div>
            </div>
            <div className="slideshow-indicators">
              {thirdCardImages.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === thirdCardSlide ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setThirdCardSlide(index);
                  }}
                />
              ))}
            </div>
          </div>

          <div
            className={`realms-option realms-slideshow ${activeCardIndex === 3 ? "active" : ""}`}
            style={{
              "--optionBackground": `url(${fourthCardImages[fourthCardSlide]})`,
            }}
            onClick={(e) => handleCardClick(e, 3)}
          >
            <div className="realms-shadow"></div>
            <div className="realms-label">
              <div className="realms-icon">
                <img src={`${process.env.PUBLIC_URL}/images/icons/nebula-icon.png`} alt="Swirling nebula skies Icon" />
              </div>
              <div className="realms-info">
                <div className="realms-main">Swirling nebula skies</div>
                <div className="realms-sub"></div>
              </div>
            </div>
            <div className="slideshow-indicators">
              {fourthCardImages.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === fourthCardSlide ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFourthCardSlide(index);
                  }}
                />
              ))}
            </div>
          </div>

          <div
            className={`realms-option realms-slideshow ${activeCardIndex === 4 ? "active" : ""}`}
            style={{
              "--optionBackground": `url(${fifthCardImages[fifthCardSlide]})`,
            }}
            onClick={(e) => handleCardClick(e, 4)}
          >
            <div className="realms-shadow"></div>
            <div className="realms-label">
              <div className="realms-icon">
                <img src={`${process.env.PUBLIC_URL}/images/icons/thornreach-icon.png`} alt="Thornreach Wilds Icon" />
              </div>
              <div className="realms-info">
                <div className="realms-main">Thornreach Wilds</div>
                <div className="realms-sub"></div>
              </div>
            </div>
            <div className="slideshow-indicators">
              {fifthCardImages.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === fifthCardSlide ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFifthCardSlide(index);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && (
        <div
          className="realms-image-modal"
          onClick={(e) => {
            e.stopPropagation();
            closeModal(e);
          }}
        >
          <div
            className="realms-modal-content"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <button
              className="realms-modal-close"
              onClick={(e) => {
                e.stopPropagation();
                closeModal(e);
              }}
            >
              <i className="fas fa-times"></i>
            </button>
            <button
              className="realms-modal-nav realms-modal-prev"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {/* Display video when showVideo is true, otherwise show image */}
            {showVideo ? (
              <video
                src={realmVideos[activeSlideshow]}
                controls
                autoPlay
                loop
                className="realms-modal-video"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={
                  [slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages][
                    activeSlideshow
                  ]?.[modalImageIndex] || slideshowImages[modalImageIndex]
                }
                alt={`Realm ${modalImageIndex + 1}`}
                className="realms-modal-image"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            <button
              className="realms-modal-nav realms-modal-next"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
            <div className="realms-modal-indicators">
              {/* Image indicators */}
              {(
                [slideshowImages, secondCardImages, thirdCardImages, fourthCardImages, fifthCardImages][
                  activeSlideshow
                ] || slideshowImages
              ).map((_, index) => (
                <div
                  key={index}
                  className={`realms-modal-indicator ${index === modalImageIndex && !showVideo ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalIndicatorClick(index);
                  }}
                />
              ))}
              {/* Video indicator */}
              <div
                className={`realms-modal-indicator video-indicator ${showVideo ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const imageArrays = [
                    slideshowImages,
                    secondCardImages,
                    thirdCardImages,
                    fourthCardImages,
                    fifthCardImages,
                  ];
                  const currentArray = imageArrays[activeSlideshow] || slideshowImages;
                  handleModalIndicatorClick(currentArray.length);
                }}
                title="Play Video"
              >
                <i className="fas fa-play"></i>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Realms;
